// built-in imports
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  lazy,
} from "react";
import { useLocation } from "react-router-dom";
import { IntlProvider } from "react-intl";

// internal imports
import { Wrapper, Container } from "./layout.styles";
import { Nav } from "../components";
import en from "../languages/en.json";

const MenuBar = lazy(() => import("../components/menu-bar/menu-bar.component"));
const FollowingCursor = lazy(() =>
  import("../components/following-cursor/following-cursor.component")
);

// const de  = lazy(() => import("../languages/de.json"))
// const en  = lazy(() => import("../languages/en.json"))

const useLocal = navigator.language;
const getDefaultLanguage = () => localStorage.getItem("language");

const checkDefaultTheme = () => {
  const isDarkTheme = localStorage.getItem("darkTheme") === "false";
  document.body.classList.toggle("dark-theme", isDarkTheme);
  return isDarkTheme;
};

const AppContext = createContext();
const Layout = ({ children }) => {
  // ----------------------start state section--------------------------------------------
  const [activeMenu, setActiveMenu] = useState("hero");
  const [isMenuBar, setIsMenuBar] = useState(false);
  const [detectScroll, setDetectScroll] = useState(0);
  const [isHover, setIsHover] = useState(false);
  const [isDarkTheme, setIsDarkTheme] = useState(checkDefaultTheme());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const [message, setMessage] = useState(en);
  const [lang, setLang] = useState(getDefaultLanguage() ?? "en");
  // ----------------------end state section--------------------------------------------
  // ----------------------start hook section--------------------------------------------
  useEffect(() => {
    checkForLanguage();
  }, [lang]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [detectScroll]);

  useEffect(() => {
    checkDefaultTheme();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  // ----------------------end hooks section--------------------------------------------

  const toggleTheme = () => {
    setIsDarkTheme(!isDarkTheme);
    localStorage.setItem("darkTheme", isDarkTheme);
    document.body.classList.toggle("dark-theme", isDarkTheme);
  };

  const checkForLanguage = () => {
    localStorage.setItem("language", lang);
    if (lang === "en") {
      setMessage(en);
    }
    if (lang === "de") {
      const de = lazy(() => import("../languages/de.json"));
      setMessage(de);
    }
  };

  const handleScroll = () => {
    if (window.scrollY > 10) {
      setIsMenuBar(true);
    } else {
      setIsMenuBar(false);
    }
    setDetectScroll(window.scrollY);
  };

  const value = {
    activeMenu,
    setActiveMenu,
    isHover,
    setIsHover,
    isDarkTheme,
    toggleTheme,
    mobileMenuOpen,
    setMobileMenuOpen,
    isMenuBar,
    lang,
    setLang,
  };
  return (
    <AppContext.Provider value={value}>
      <IntlProvider locale={useLocal} messages={message}>
        <Wrapper>
          <Nav />
          <Container>{children}</Container>
          <FollowingCursor />
          <MenuBar />
        </Wrapper>
      </IntlProvider>
    </AppContext.Provider>
  );
};
export const useAppContext = () => useContext(AppContext);
export default Layout;
